import { useRef, useState } from "react";
import { api } from "../../api/apiClient";
import type { OneTimeExpenseRecord } from "../../api/types";
import { ONE_TIME_CATEGORY_LABEL } from "./expenseCategories";
import { euro, formatDayLabel } from "./opcostFormat";
import { businessDayKey } from "../../utils/businessDay";
import { openBlobInNewTab } from "../../utils/openBlobInNewTab";

/**
 * G-135 (OD-175): one line of the One-off expenses list. Description and
 * amount, then "date · type" (plus shop and who filed it when the owner is
 * looking across shops), then the paperwork: the receipt, and either the
 * invoice (opens the PDF) or "Add invoice" — a file picker that attaches it
 * now and refreshes the list. The owner also sees a "No invoice" warning,
 * and "No invoice will come" (OD-177), which takes the row off the warning
 * with a reason; an invoice attached later still goes on.
 */
export function ExpenseRow({
  row,
  isOwner,
  showWhere,
  onViewReceipt,
  onEdit,
  onDelete,
  onInvoiceAttached,
  onMarkNoInvoice,
}: {
  row: OneTimeExpenseRecord;
  isOwner: boolean;
  /** Owner on all shops: say which shop and who filed it. */
  showWhere: boolean;
  onViewReceipt: (row: OneTimeExpenseRecord) => void;
  onEdit: (row: OneTimeExpenseRecord) => void;
  onDelete: (row: OneTimeExpenseRecord) => void;
  onInvoiceAttached: () => Promise<void>;
  /** OD-177: owner only. */
  onMarkNoInvoice: (row: OneTimeExpenseRecord) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  // Shown under this row, not in a page-level alert far from where it happened.
  const [error, setError] = useState<string | null>(null);

  const openInvoice = async () => {
    setError(null);
    try {
      await openBlobInNewTab(() => api.fetchOneTimeExpenseInvoiceBlobUrl(row.id));
    } catch {
      setError("Could not open the invoice.");
    }
  };

  const attach = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      await api.attachOneTimeExpenseInvoice(row.id, file);
      await onInvoiceAttached();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not attach the invoice.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const meta = [
    formatDayLabel(row.incurredOn),
    ONE_TIME_CATEGORY_LABEL[row.category],
    ...(showWhere ? [row.location.name, `by ${row.createdBy.fullName}`] : []),
  ].join(" · ");

  return (
    <li className="opcost-row">
      <div className="opcost-row-main">
        <span className="opcost-row-title">
          {row.description}
          <span className="opcost-row-amount">{euro(row.amountEur)}</span>
        </span>
        <span className="opcost-row-meta">{meta}</span>
        {row.notes ? <span className="opcost-row-notes">{row.notes}</span> : null}
        <span className="opcost-row-docs">
          {row.hasReceipt ? (
            <button
              type="button"
              className="button plain opcost-doc-link"
              onClick={() => onViewReceipt(row)}
            >
              Receipt
            </button>
          ) : null}
          {row.hasInvoice ? (
            <button
              type="button"
              className="button plain opcost-doc-link"
              onClick={() => void openInvoice()}
            >
              Invoice
            </button>
          ) : (
            <>
              <input
                ref={fileRef}
                className="exp-file-input"
                type="file"
                accept="application/pdf"
                aria-label={`Invoice PDF for ${row.description}`}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void attach(file);
                }}
              />
              <button
                type="button"
                className="button plain opcost-doc-link"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                {busy ? "Adding…" : "Add invoice"}
              </button>
              {row.noInvoice ? null : isOwner ? (
                <>
                  <span className="badge warning">No invoice</span>
                  <button
                    type="button"
                    className="button plain opcost-doc-link"
                    onClick={() => onMarkNoInvoice(row)}
                  >
                    No invoice will come
                  </button>
                </>
              ) : null}
            </>
          )}
        </span>
        {!row.hasInvoice && row.noInvoice ? (
          <span className="opcost-row-no-invoice">
            No invoice will come — {row.noInvoice.reason} ({row.noInvoice.by.fullName},{" "}
            {formatDayLabel(businessDayKey(new Date(row.noInvoice.at)))})
          </span>
        ) : null}
        {error ? (
          <span className="opcost-row-error" role="alert">
            {error}
          </span>
        ) : null}
      </div>
      {isOwner ? (
        <div className="opcost-row-actions">
          <button type="button" className="button plain" onClick={() => onEdit(row)}>
            Edit
          </button>
          <button type="button" className="button plain" onClick={() => onDelete(row)}>
            Delete
          </button>
        </div>
      ) : null}
    </li>
  );
}
